import type { CreateResourceDto, Resource, ResourceTheme, S3File } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	TextArea,
} from "@dune/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import { S3FileInput } from "../../../component/common/S3FileInput";
import {
	useCreateResourceMutation,
	useUpdateResourceMutation,
} from "../../../api/mutations/resources.mutations";
import { useGetResourceThemes } from "../../../api/queries/resources.queries";
import { z } from "zod";

const addResourceFormSchema = z.object({
	themeId: z.string().min(1, "Le thème est requis"),
	name: z.string().trim().min(1, "Le libellé est requis"),
	description: z.string().optional(),
	file: z.custom<S3File>((value) => value !== undefined && value !== null, {
		message: "Le fichier est requis",
	}),
});

type AddResourceFormValues = z.infer<typeof addResourceFormSchema>;

interface AddResourceDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	resourceToEdit?: Resource | null;
}

export const AddResourceDialog = ({
	open,
	onOpenChange,
	resourceToEdit,
}: AddResourceDialogProps) => {
	const isEditMode = Boolean(resourceToEdit);
	const createResourceMutation = useCreateResourceMutation();
	const updateResourceMutation = useUpdateResourceMutation();
	const { data: themes = [], isLoading: isLoadingThemes } = useGetResourceThemes({
		type: "DOCUMENT",
	});

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, isValid },
	} = useForm<AddResourceFormValues>({
		resolver: zodResolver(addResourceFormSchema),
		mode: "onChange",
		defaultValues: {
			themeId: "",
			name: "",
			description: "",
			file: undefined,
		},
	});

	useEffect(() => {
		if (open && resourceToEdit) {
			const bucket = resourceToEdit.file.bucket === "public" ? "public" : "private";
			reset({
				themeId: resourceToEdit.themeId.toString(),
				name: resourceToEdit.name,
				description: resourceToEdit.description ?? "",
				file: {
					bucket,
					key: resourceToEdit.file.key,
					name: resourceToEdit.file.name,
					type: resourceToEdit.file.mimeType,
					size: resourceToEdit.file.size ?? 0,
				},
			});
			return;
		}

		if (open && !resourceToEdit) {
			reset({
				themeId: "",
				name: "",
				description: "",
				file: undefined,
			});
		}
	}, [open, resourceToEdit, reset]);

	const handleClose = (nextOpen: boolean) => {
		if (!nextOpen) {
			reset({
				themeId: "",
				name: "",
				description: "",
				file: undefined,
			});
		}
		onOpenChange(nextOpen);
	};

	const handleUploadSuccess = (uploadedFile: S3File) => {
		setValue("file", uploadedFile, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const handleSubmitResource = (values: AddResourceFormValues) => {
		const payload: CreateResourceDto = {
			type: "DOCUMENT",
			themeId: Number(values.themeId),
			name: values.name.trim(),
			description: values.description?.trim() || undefined,
			file: values.file,
		};

		if (resourceToEdit) {
			updateResourceMutation.mutate(
				{
					resourceId: resourceToEdit.id,
					payload,
				},
				{
					onSuccess: () => {
						handleClose(false);
					},
				},
			);
			return;
		}

		createResourceMutation.mutate(payload, {
			onSuccess: () => {
				handleClose(false);
			},
		});
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="w-[min(980px,92vw)] max-w-[980px]">
				<DialogHeader>
					<DialogTitle>
						{isEditMode ? "Modifier une ressource" : "Ajouter une ressource"}
					</DialogTitle>
				</DialogHeader>

				<form
					onSubmit={handleSubmit(handleSubmitResource)}
					className="space-y-5 py-2"
				>
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label>Type</Label>
							<Input value="Documentation" readOnly disabled />
						</div>

						<div className="space-y-2">
							<Label>Thème</Label>
							<Controller
								name="themeId"
								control={control}
								render={({ field }) => (
									<Select
										value={field.value}
										onValueChange={field.onChange}
										disabled={isLoadingThemes}
									>
										<SelectTrigger>
											<SelectValue placeholder="Sélectionnez un thème" />
										</SelectTrigger>
										<SelectContent>
											{themes.map((theme: ResourceTheme) => (
												<SelectItem
													key={theme.id}
													value={theme.id.toString()}
												>
													{theme.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
							{errors.themeId && (
								<p className="text-sm text-red-500">{errors.themeId.message}</p>
							)}
						</div>
					</div>

					<div className="space-y-2">
						<Label>Libellé</Label>
						<Controller
							name="name"
							control={control}
							render={({ field }) => (
								<Input {...field} placeholder="Nom de la ressource" />
							)}
						/>
						{errors.name && (
							<p className="text-sm text-red-500">{errors.name.message}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label>Fichier</Label>
						<S3FileInput
							key={`resource-upload-${open}-${resourceToEdit?.id ?? "create"}`}
							bucket="private"
							onUploadSuccess={handleUploadSuccess}
							hidePreviewButton
							initialFile={
								resourceToEdit
									? {
											bucket:
												resourceToEdit.file.bucket === "public"
													? "public"
													: "private",
											key: resourceToEdit.file.key,
											name: resourceToEdit.file.name,
											type: resourceToEdit.file.mimeType,
											size: resourceToEdit.file.size ?? 0,
										}
									: undefined
							}
						/>
						{errors.file && (
							<p className="text-sm text-red-500">{errors.file.message}</p>
						)}
					</div>

					<div className="space-y-2">
						<Label>Description</Label>
						<Controller
							name="description"
							control={control}
							render={({ field }) => (
								<TextArea
									{...field}
									rows={5}
									placeholder="Description de la ressource"
								/>
							)}
						/>
					</div>

					<DialogFooter className="pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => handleClose(false)}
						>
							Annuler
						</Button>
						<Button
							type="submit"
							disabled={
								!isValid ||
								createResourceMutation.isPending ||
								updateResourceMutation.isPending
							}
							isLoading={
								createResourceMutation.isPending ||
								updateResourceMutation.isPending
							}
						>
							{isEditMode ? "Modifier" : "Ajouter"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
